<template>
  <div style="display: none">
    <div id="ledger-pdf-content" class="ledger-pdf-container">
      <!-- Organization Header -->
      <div class="header-section mb-6">
        <div class="flex justify-between items-start mb-2">
          <div class="logo-text">
            <h1 class="text-xl font-bold text-[#1e3a8a]">{{ orgName || 'ZAVRON FINANCE PVT LTD' }}</h1>
            <p class="text-[9px] italic">Statement of Account of Loan Number {{ selectedCase?.caseNo }}</p>
          </div>
          <div class="text-right text-[10px]">
            {{ $dayjs().format('DD-MM-YYYY hh:mm:ss a') }}
          </div>
        </div>
        
        <div class="org-details text-center border-t border-black pt-2">
          <h2 class="text-[12px] font-bold uppercase">{{ orgName || 'Zavron Finance Pvt Ltd' }}</h2>
          <p class="text-[9px] leading-tight">
            Registered Office: 125,Zavron finance pvt ltd, near mure memorial hospital, maharajbagh road,sitabuldi Nagpur, Maharastra, 440012 .<br />
            Branch Address: NAGPUR, 125,near mure memorial hospital,maharajbagh road, Area- SITABULDI, Town- NAGPUR, PIN Code- 440012<br />
            CIN : U67100MH2017PTC292183
          </p>
        </div>
      </div>

      <h3 class="text-center text-[#00A1E4] text-lg font-normal border-y border-gray-300 py-1 mb-6">
        Statement of Account
      </h3>

      <!-- I - Summary of Loan -->
      <div class="mb-6">
        <h4 class="text-[#00A1E4] text-md mb-2">I - Summary of Loan</h4>
        <div class="grid grid-cols-2 gap-x-8 gap-y-1 text-[11px]">
          <div class="flex"><span class="w-32 font-semibold">Loan Account No</span>: {{ selectedCase?.caseNo }}</div>
          <div class="flex"><span class="w-32 font-semibold">Branch</span>: {{ branchName }}</div>
          <div class="flex"><span class="w-32 font-semibold">Product Type</span>: {{ selectedCase?.loanDetails?.caseType || 'Personal Loan' }}</div>
          <div class="flex"><span class="w-32 font-semibold">Loan Type</span>: {{ selectedCase?.loanDetails?.loanType || 'UnSecured Loan' }}</div>
          <div class="flex"><span class="w-32 font-semibold">Financing Amount</span>: {{ formatCurrency(selectedCase?.loanDetails?.loanAmountSanctioned) }}</div>
        </div>
      </div>

      <!-- Customer Details -->
      <div class="mb-6">
        <h4 class="text-[#00A1E4] text-md mb-2">Customer Details</h4>
        <table class="w-full pdf-table text-[10px]">
          <thead>
            <tr class="bg-gray-50 font-bold">
              <th class="px-2 py-1 text-left w-[15%]">CIF</th>
              <th class="px-2 py-1 text-left w-[10%]">Type</th>
              <th class="px-2 py-1 text-left w-[25%]">Name</th>
              <th class="px-2 py-1 text-left w-[35%]">Address</th>
              <th class="px-2 py-1 text-left w-[15%]">Mobile No</th>
            </tr>
          </thead>
          <tbody>
            <!-- Borrower -->
            <tr>
              <td class="px-2 py-1">{{ selectedCase?.customer?.crn || '-' }}</td>
              <td class="px-2 py-1">Borrower</td>
              <td class="px-2 py-1">{{ selectedCase?.customer?.customerFullName }}</td>
              <td class="px-2 py-1">{{ borrowerAddress }}</td>
              <td class="px-2 py-1">{{ borrowerMobile }}</td>
            </tr>
            <!-- Guarantor(s) -->
            <tr v-for="(g, idx) in guarantors" :key="'g-'+idx">
              <td class="px-2 py-1">{{ g.crn || '-' }}</td>
              <td class="px-2 py-1">Guarantor</td>
              <td class="px-2 py-1">{{ g.customerFullName || g.name }}</td>
              <td class="px-2 py-1">{{ getAddress(g) }}</td>
              <td class="px-2 py-1">{{ getMobile(g) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Ledger Details -->
      <div class="mb-6">
        <h4 class="text-[#00A1E4] text-md mb-2">Ledger Details</h4>
        <table class="w-full pdf-table text-[9px]">
          <thead>
            <tr class="bg-gray-100 font-bold">
              <th class="px-1 py-1 text-left w-[10%]">Date</th>
              <th class="px-1 py-1 text-left w-[25%]">Particulars</th>
              <th class="px-1 py-1 text-left w-[12%]">Voucher Type</th>
              <th class="px-1 py-1 text-left w-[10%]">Payment Mode</th>
              <th class="px-1 py-1 text-left w-[10%]">Voucher No</th>
              <th class="px-1 py-1 text-left w-[10%]">Ins No</th>
              <th class="px-1 py-1 text-right w-[8%]">Debit</th>
              <th class="px-1 py-1 text-right w-[8%]">Credit</th>
              <th class="px-1 py-1 text-right w-[12%]">Balance</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(entry, index) in ledgerEntries" :key="index">
              <td class="px-2 py-3">{{ formatDate(entry.date) }}</td>
              <td class="px-2 py-3 truncate-particulars">{{ entry.particulars }}</td>
              <td class="px-2 py-3">{{ entry.voucherType }} Voucher</td>
              <td class="px-2 py-3">{{ entry.postingType === 'Auto' ? 'Journal' : 'Cash/Bank' }}</td>
              <td class="px-2 py-3">{{ entry.voucherNo }}</td>
              <td class="px-2 py-3">{{ entry.instrumentNo }}</td>
              <td class="px-2 py-3 text-right">{{ entry.debit > 0 ? formatCurrency(entry.debit) : '0' }}</td>
              <td class="px-2 py-3 text-right">{{ entry.credit > 0 ? formatCurrency(entry.credit) : '0' }}</td>
              <td class="px-2 py-3 text-right font-semibold">{{ formatCurrency(entry.balance) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="bg-gray-100 font-bold">
              <td colspan="6" class="px-2 py-3 text-right uppercase">Total</td>
              <td class="px-2 py-3 text-right">{{ formatCurrency(totalDebit) }}</td>
              <td class="px-2 py-3 text-right">{{ formatCurrency(totalCredit) }}</td>
              <td class="px-2 py-3 text-right">{{ formatCurrency(finalBalance) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import html2pdf from 'html2pdf.js'
import { getFullAddress } from '@/utils/common'

export default {
  name: 'CaseLedgerPDF',
  props: {
    selectedCase: Object,
    ledgerEntries: Array,
    orgName: String,
    branchName: {
      type: String,
      default: 'NAGPUR'
    }
  },
  computed: {
    totalDebit() {
      return this.ledgerEntries.reduce((sum, e) => sum + (e.debit || 0), 0)
    },
    totalCredit() {
      return this.ledgerEntries.reduce((sum, e) => sum + (e.credit || 0), 0)
    },
    finalBalance() {
      return Math.abs(this.totalDebit - this.totalCredit)
    },
    borrowerAddress() {
      const customer = this.selectedCase?.customer
      if (!customer) return 'N/A'
      const addr = customer.customerAddress && customer.customerAddress.length > 0
        ? customer.customerAddress[0]
        : customer
      return getFullAddress(addr)
    },
    borrowerMobile() {
      const customer = this.selectedCase?.customer
      return customer?.customerPhoneNumber || (Array.isArray(customer?.customerMobile) ? customer.customerMobile[0] : 'N/A')
    },
    guarantors() {
      return (this.selectedCase?.guarantors && this.selectedCase.guarantors.length > 0
        ? this.selectedCase.guarantors
        : this.selectedCase?.loanDetails?.guarantors) || []
    }
  },
  methods: {
    getAddress(person) {
      if (!person) return 'N/A'
      const addr = person.customerAddress && person.customerAddress.length > 0
        ? person.customerAddress[0]
        : person
      return getFullAddress(addr)
    },
    getMobile(person) {
      if (!person) return 'N/A'
      return person.customerPhoneNumber || (Array.isArray(person.customerMobile) ? person.customerMobile[0] : 'N/A')
    },
    formatDate(date) {
      return date ? this.$dayjs(date).format('DD-MM-YYYY') : '-'
    },
    formatCurrency(val) {
      return Number(val || 0).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    async generatePDF() {
      const element = document.getElementById('ledger-pdf-content')
      const opt = {
        margin: [0.5, 0.5, 0.5, 0.5],
        filename: `Ledger_${this.selectedCase?.caseNo || 'Statement'}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 3, useCORS: true, logging: false },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      }

      try {
        await html2pdf().set(opt).from(element).save()
      } catch (error) {
        console.error('PDF generation failed:', error)
      }
    }
  }
}
</script>

<style scoped>
.ledger-pdf-container {
  padding: 30px;
  background: white;
  color: black;
  font-family: Arial, Helvetica, sans-serif;
  line-height: 1.5;
}
.pdf-table {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
  table-layout: fixed;
  border-top: 1px solid #666;
  border-left: 1px solid #666;
}
.pdf-table th,
.pdf-table td {
  border-bottom: 1px solid #666;
  border-right: 1px solid #666;
  padding: 12px 8px 15px 8px; /* Extra bottom padding */
  vertical-align: top;
  word-wrap: break-word;
  overflow-wrap: break-word;
}
.truncate-particulars {
  word-break: break-all;
  white-space: normal;
}
</style>
